// This browser's side of notifications (features/notifications.md): whether it can get them, its push
// subscription, and turning them on and off. The server keeps which devices are on (api.ts).

import { installed, isIPhone } from "./offline.ts";
import { fromBase64Url, type Renewed, renew } from "./push-key.ts";

// What this device can do about notifications: nothing ("unsupported"), not until Tripmo is on the
// Home Screen (an iPhone in Safari), not while the browser blocks them, or they're off or on.
export type DeviceState = "unsupported" | "needs-install" | "blocked" | "off" | "on";

export const canPush = () => "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

// Subscribing again with a new key, one at a time (opening, coming back into view and a tap can meet),
// and read only once it's done: two at once would each make a subscription, and one would be lost.
let renewing: Promise<unknown> = Promise.resolve();
function oneAtATime<T>(work: () => Promise<T>): Promise<T> {
  const next = renewing.then(work, work);
  renewing = next.catch(() => {});
  return next;
}

export async function pushSubscription(): Promise<PushSubscription | null> {
  if (!canPush()) return null;
  await renewing;
  const registration = await navigator.serviceWorker.getRegistration();
  return (await registration?.pushManager.getSubscription()) ?? null;
}

export async function deviceState(): Promise<DeviceState> {
  if (!canPush()) return isIPhone() && !installed() ? "needs-install" : "unsupported";
  if (Notification.permission === "denied") return "blocked";
  return Notification.permission === "granted" && (await pushSubscription()) ? "on" : "off";
}

// This device's subscription, made again if Tripmo's key has changed since it was made (the key pair
// replaced): null when notifications are off here.
export async function renewSubscription(key: string): Promise<Renewed<PushSubscription> | null> {
  if (!canPush() || Notification.permission !== "granted") return null;
  const registration = await navigator.serviceWorker.getRegistration();
  if (!registration) return null;
  return oneAtATime(() => renew(registration.pushManager, key));
}

// After the person's tap: the browser asks them, then subscribes with Tripmo's key (again, if Tripmo's
// key changed). Gives the subscription to keep, or what stopped it.
export async function subscribe(key: string): Promise<Renewed<PushSubscription> | "blocked" | "off"> {
  const permission = await Notification.requestPermission();
  if (permission !== "granted") return permission === "denied" ? "blocked" : "off";
  const { pushManager } = await navigator.serviceWorker.ready;
  return oneAtATime(async () => {
    const renewed = await renew(pushManager, key, { ifUnknown: true });
    if (renewed) return renewed;
    const subscription = await pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: fromBase64Url(key),
    });
    return { subscription, replaces: null };
  });
}

// The page as a phone or a computer, for "on this phone".
export const deviceWord = () => (matchMedia("(pointer: coarse)").matches ? "phone" : "computer");
